import http from "./http/index";
import { Capacitor } from '@capacitor/core';
import { getAriaAppBaseUrl, getAriaPathPrefix } from '../utils/ariaUrls';
import { getHttpStatus, isTransientServerError } from '../utils/sessionAuth';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const Auth = {
  loginWithEmail: async (email: string, password: string, token?: string) => {
    // Include hostname and pathname in request so server can validate platform access
    // For Capacitor apps, window.location.hostname is 'localhost' or empty, so we need to explicitly set it
    let hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    let pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Capacitor apps use localhost or capacitor:// URLs, so override with the real platform hostname
    if (Capacitor.isNativePlatform()) {
      const isPortal = process.env.REACT_APP_TARGET === 'portal';

      if (isPortal) {
        hostname = 'app.aptiveindex.com';
        pathname = '/login';
      } else {
        const ariaBaseUrl = getAriaAppBaseUrl();
        const ariaPathPrefix = getAriaPathPrefix();
        try {
          const url = new URL(ariaBaseUrl);
          hostname = url.hostname;
          pathname = ariaPathPrefix || '/aria';
        } catch (error) {
          hostname = 'app.ariachat.ai';
          pathname = '/aria';
        }
      }
    }

    const payload = {
      email,
      password,
      ssotoken: token,
      hostname,
      pathname,
    };

    try {
      const res = await http.post<any>("/api/auth/login", payload);
      return res.data;
    } catch (error) {
      const status = getHttpStatus(error);
      if (isTransientServerError(status)) {
        await sleep(1500);
        const retry = await http.post<any>("/api/auth/login", payload);
        return retry.data;
      }
      throw error;
    }
  },
  refreshSession: async () => {
    return http.post<{ token: string; message: string }>('/api/auth/refresh', {}).then((res) => res.data);
  },
  loginByToken: async (token: string) => {
    return http
      .post<any>(`/api/auth/login-by-token`, { token })
      .then(res => res.data)
      .catch(error => {
        console.error(error);
        throw error;
      });
  },
  passwordResetToken: async (email?: string, token?: string | null, userRole: string = "") => { 
    return http
      .post<any>("/api/auth/mail-reset-token", {
        ...(email ? { email } : {}),
        ...(token ? { token } : {}),
        ...(userRole !== "" ? { userRole } : {}),
      })
      .then((res) => res);
  },
  passwordReset: async (newPassword: string, confirmPassword: string, token: string)=>{
    return http
    .post<any>("/api/auth/reset-forgot-password", {
      newPassword, 
      confirmPassword,
      token
    })
    .then((res)=>res)
  },
  passwordResetWithoutResetToken: async (newPassword: string, confirmPassword: string, email: string)=>{
    return http
    .post<any>("/api/auth/change-password-without-reset-token", {
      newPassword, 
      confirmPassword,
      email
    })
    .then((res)=>res)
  },
  resetPassword: async (userId: string, currentPassword: string, newpassword: string, entityId?: string) => {
    return http
      .post(`/api/auth/resetpassword`, { userId, currentPassword, newpassword, ...(entityId ? { entityId } : {}) })
      .then(res => res.data)
      .catch(error => {
        console.error(error);
        throw error;
      });
  },
  setNewPassword: async (newPassword: string, confirmPassword: string) => {
    return http
      .post(`/api/auth/set-new-password`, { newPassword, confirmPassword })
      .then(res => res.data)
      .catch(error => {
        console.error(error);
        throw error;
      });
  },
  getResetToken: async (token: string) => {
    return http
      .get(`/api/auth/reset-forgot-password/${token}`)
      .then(res => res.data)
      .catch(error => {
        console.error(error);
        throw error;
      });
  }, 
  getResetTokenByEmail: async (email: string) => {
    return http
      .get(`/api/auth/get-reset-token-by-email`, { params: { email } })
      .then((res) => res.data.resetToken)
      .catch(error => {
        console.error(error);
        throw error;
      });
  },
  switchEntity: async (entityId: string) => {
    return http
      .post(`/api/auth/switch-entity`, { entityId })
      .then(res => res.data)
      .catch(error => {
        console.error('Error switching entity:', error);
        throw error;
      });
  },
  setDefaultEntity: async (entityId: string) => {
    return http
      .post(`/api/auth/default-entity`, { entityId })
      .then(res => res.data)
      .catch(error => {
        console.error('Error setting default entity:', error);
        throw error;
      });
  },
  logout: async (): Promise<void> => {
    try {
        await http.post('/api/auth/logout', {});
    } catch (error) {
        console.error('Logout API error:', error);
        // Don't throw - we still want to clear local session
    }
}
};

export default Auth;